import { GetSelectedProductResponse, ProductInfo } from "src/model/ProductMatch";
import React from "react";
import {
    KatLink,
    KatLabel,
    KatStarRating,
    KatDropdown,
    KatIcon,
    KatOption,
    KatButton,
} from "@amzn/katal-react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import {withTranslation, WithTranslation} from "react-i18next";
import {getCountryListConfig, getCurrencySymbolConfig} from "src/config/Config";
import {setSwitchStoreStart} from "src/actions/SelectedProductQueryAction";
import MetricsUtil from "src/utils/MetricsUtil";
import ApplicationState from "src/model/ApplicationState";
import ProductDataValidator from "src/utils/ProductDataValidator";
import ErrorStatusComponent from "src/components/common/ErrorStatusComponent";
import FormatUtil from "src/utils/FormatUtil";

export interface ProductDetailProps {
    countryCode: string,
    handleSelect: Function
}

interface StateProps {
    selectedProductRes: GetSelectedProductResponse,
}

interface OwnProps {
    readonly metricsUtil?: MetricsUtil,
    handleCountryChange: (countryCode: string) => void
}

interface DispatchProps {
    readonly dispatchSwitchStore: (countryCode: string, selectedProduct: ProductInfo) => void,
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    selectedProductRes: state.selectedProductRes,
});

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({
        dispatchSwitchStore: setSwitchStoreStart,
    }, dispatch)
);

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = StateProps & ProductDetailProps & OwnProps & DispatchProps & WithTranslation;

export class ProductDetailComponent extends React.Component<Props, {}> {

    private readonly metricsUtil: MetricsUtil;

    constructor(props: Props) {
        super(props);

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }
    }

    getCurrencySymbol(): string {
        return getCurrencySymbolConfig(this.props.countryCode);
    };

    handleMarketPlaceChange = (event: KatDropdown.ChangeEvent) => {
        this.props.handleCountryChange(event.detail.value);
        this.props.dispatchSwitchStore(event.detail.value, this.props.selectedProductRes.data.selectedProduct);
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("switchAmazonStore", "click");
        }
    }

    render() {
        const {t, selectedProductRes, countryCode} = this.props;
        const product = selectedProductRes.data.selectedProduct;

        return (
            <>
            <div className="product-detail">
                <div className="product-detail-content">
                    <div id="product-detail-left" className="product-detail-left">
                        <img src={product.imageUrl} className="product-detail-img"/>
                        <table className={window.innerWidth > 1440 ? 'product-detail-table-left' : 'product-detail-table-left product-detail-table-left-slim-width' }>
                            <thead>
                                <tr>
                                    <td colSpan={2}><KatLink label={product.title} className="product-detail-title" href={product.link} target="_blank" variant="link" /></td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><KatLabel text={t('SC_FBA_Calc_productInfo_ASIN')} className="bold-label"/></td>
                                    <td>{product.asin}</td>
                                </tr>
                                <tr>
                                    <td><KatLabel text={t('SC_FBA_Calc_productInfo_shipWeight')} className="bold-label"/></td>
                                    <td>{FormatUtil.formatNumberDisplay(product.weight)} {product.weightUnitStringId? t(product.weightUnitStringId) : ''}</td>
                                </tr>
                                <tr>
                                    <td><KatLabel text={t('SC_FBA_Calc_package_dimensions')} className="bold-label"/></td>
                                    <td>{FormatUtil.formatNumberDisplay(product.length)} X {FormatUtil.formatNumberDisplay(product.width)} X {FormatUtil.formatNumberDisplay(product.height)} {product.dimensionUnitStringId ? t(product.dimensionUnitStringId) : ''}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div id="divider-line" className="vertical-line"/>
                    <div id="product-detail-right">
                        <table className={window.innerWidth > 1440 ? 'product-detail-table-right': 'product-detail-table-right product-detail-table-right-slim-width'}>
                            <tbody>
                                <tr>
                                    <td/>
                                    <td/>
                                    <td/>
                                    {product.winOfferMerchantName && <>
                                        <td colSpan={2}><KatLabel text={t('SC_FBA_Calc_title_details')}  className="bold"/></td>
                                    </>}
                                </tr>
                                <tr>
                                    <td><KatLabel text={t('SC_FBA_Calc_label_marketplace')} className="bold-label" /></td>
                                    <td>
                                        <span className="product-detail-table-right-center">
                                            <KatDropdown rich-selection-label value={countryCode} size="small" className="country-dropdown"
                                                            onChange={this.handleMarketPlaceChange} >
                                                {
                                                    getCountryListConfig().map((country, index) => (
                                                        <KatOption value={country} key={index}>
                                                            <div className="flexed">
                                                                <KatIcon name={("flag-" + country.toLowerCase()) as KatIcon.Name} size="small" />
                                                                <span className="paddedMedium">{country}</span>
                                                            </div>
                                                        </KatOption>
                                                    ))
                                                }
                                            </KatDropdown>
                                        </span>
                                    </td>
                                    <td/>
                                    {product.winOfferMerchantName && <>
                                        <td><KatLabel text={t('SC_FBA_Calc_label_pricePlusShipping')} className="bold-label"/></td>
                                        <td>{this.getCurrencySymbol()}{FormatUtil.formatNumberDisplay(product.winOfferPriceAmount)} + {FormatUtil.formatNumberDisplay(product.winOfferShippingAmount)}</td>
                                    </>}
                                </tr>
                                <tr>
                                    <td><KatLabel text={t('SC_FBA_Calc_label_salesRank')} className="bold-label"/></td>
                                    <td>{product.salesRank == undefined ? '-' : '#' + FormatUtil.formatNumberDisplay(product.salesRank) +', (' + product.salesRankContextName + ')'}</td>
                                    <td/>
                                    {product.winOfferMerchantName && <>
                                        <td><KatLabel text={t('SC_FBA_Calc_label_condition')} className="bold-label"/></td>
                                        <td>{product.winOfferCondition}</td>
                                    </>}
                                </tr>
                                <tr>
                                    <td><KatLabel text={t('SC_FBA_Calc_label_offers')} className="bold-label"/></td>
                                    <td>{product.offerCount == undefined? 0 : FormatUtil.formatNumberDisplay(product.offerCount)} {t('SC_FBA_Calc_content_offers')}</td>
                                    <td/>
                                    {product.winOfferMerchantName && <>
                                        <td><KatLabel text={t('SC_FBA_Calc_label_shipFrom')} className="bold-label" /></td>
                                        <td>{product.winOfferFulfillerName}</td>
                                    </>}
                                </tr>
                                <tr>
                                    <td><KatLabel text={t('SC_FBA_Calc_label_rating')} className="bold-label" /></td>
                                    <td>
                                        <span className="product-detail-table-right-center">
                                            <KatStarRating value={product.customerReviewsRatingValue ? product.customerReviewsRatingValue: 0}
                                                           stars={5} className="rating"/>
                                            <span className="paddedLittle">{product.customerReviewsCount == undefined ? 0 : FormatUtil.formatNumberDisplay(product.customerReviewsCount)}</span>
                                        </span>
                                    </td>
                                    <td/>
                                    {product.winOfferMerchantName && <>
                                        <td><KatLabel text={t('SC_FBA_Calc_label_soldBy')} className="bold-label"/></td>
                                        <td>{product.winOfferMerchantName}</td>
                                    </>}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div>
                    <KatButton variant="secondary" label={t('SC_FBA_Calc_link_searchAnotherProd')} onClick={(event) => {
                        event.preventDefault();
                        this.props.handleSelect(null);
                    }} />
                </div>
            </div>
            {!ProductDataValidator.isProductValid(product) && <ErrorStatusComponent description={t('SC_FBA_Calc_error_productPropertyMissing')} />}
            </>
        );
    }
}

export default withTranslation()(connector(ProductDetailComponent));